/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  adminUsersApi,
  adminOrdersApi,
  adminAnalyticsApi,
  adminRecipesApi,
  adminMenusApi,
  adminSubscriptionsApi,
} from '../../services/adminApi';

interface AdminState {
  dashboardStats: any | null;
  users: any[];
  usersTotal: number;
  orders: any[];
  ordersTotal: number;
  recipes: any[];
  recipesTotal: number;
  menus: any[];
  subscriptions: any[];
  plans: any[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AdminState = {
  dashboardStats: null,
  users: [],
  usersTotal: 0,
  orders: [],
  ordersTotal: 0,
  recipes: [],
  recipesTotal: 0,
  menus: [],
  subscriptions: [],
  plans: [],
  isLoading: false,
  error: null,
};

export const fetchDashboardStats = createAsyncThunk(
  'admin/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      return await adminAnalyticsApi.getDashboardStats();
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchAdminUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      return await adminUsersApi.getUsers(params);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch users');
    }
  }
);

export const fetchAdminOrders = createAsyncThunk(
  'admin/fetchOrders',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      return await adminOrdersApi.getOrders(params);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch orders');
    }
  }
);

export const fetchAdminRecipes = createAsyncThunk(
  'admin/fetchRecipes',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      return await adminRecipesApi.getRecipes(params);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch recipes');
    }
  }
);

export const fetchAdminMenus = createAsyncThunk(
  'admin/fetchMenus',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      return await adminMenusApi.getWeeklyMenus(params);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch menus');
    }
  }
);

export const fetchAdminSubscriptions = createAsyncThunk(
  'admin/fetchSubscriptions',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      return await adminSubscriptionsApi.getUserSubscriptions(params);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch subscriptions');
    }
  }
);

export const fetchSubscriptionPlans = createAsyncThunk(
  'admin/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      return await adminSubscriptionsApi.getPlans();
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch plans');
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard stats
    builder.addCase(fetchDashboardStats.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchDashboardStats.fulfilled, (state, action) => {
      state.isLoading = false;
      state.dashboardStats = action.payload;
    });
    builder.addCase(fetchDashboardStats.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Users
    builder.addCase(fetchAdminUsers.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchAdminUsers.fulfilled, (state, action) => {
      state.isLoading = false;
      state.users = action.payload?.items || action.payload || [];
      state.usersTotal = action.payload?.total || 0;
    });
    builder.addCase(fetchAdminUsers.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Orders
    builder.addCase(fetchAdminOrders.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchAdminOrders.fulfilled, (state, action) => {
      state.isLoading = false;
      state.orders = action.payload?.items || action.payload || [];
      state.ordersTotal = action.payload?.total || 0;
    });
    builder.addCase(fetchAdminOrders.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Recipes
    builder.addCase(fetchAdminRecipes.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchAdminRecipes.fulfilled, (state, action) => {
      state.isLoading = false;
      state.recipes = action.payload?.items || action.payload || [];
      state.recipesTotal = action.payload?.total || 0;
    });
    builder.addCase(fetchAdminRecipes.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Menus
    builder.addCase(fetchAdminMenus.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchAdminMenus.fulfilled, (state, action) => {
      state.isLoading = false;
      state.menus = action.payload?.items || action.payload || [];
    });
    builder.addCase(fetchAdminMenus.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Subscriptions
    builder.addCase(fetchAdminSubscriptions.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchAdminSubscriptions.fulfilled, (state, action) => {
      state.isLoading = false;
      state.subscriptions = action.payload?.items || action.payload || [];
    });
    builder.addCase(fetchAdminSubscriptions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Plans
    builder.addCase(fetchSubscriptionPlans.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
      state.isLoading = false;
      state.plans = action.payload || [];
    });
    builder.addCase(fetchSubscriptionPlans.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearAdminError } = adminSlice.actions;
export default adminSlice.reducer;
